import { v4 as uuidv4 } from 'uuid';

import { KnmResultInfoTypeCodesEnum } from 'APIPackage/CatalogsModule/enums/KnmResultInfoTypeCodesEnum';
import { IKnmInjunction } from 'APIPackage/KnmModule/interfaces/IKnmInjunction';
import { IKnmObjectParams } from 'APIPackage/KnmModule/interfaces/IKnmObjectParams';
import { IKnmResult } from 'APIPackage/KnmModule/interfaces/IKnmResult';
import { IKnmResultInfo } from 'APIPackage/KnmModule/interfaces/IKnmResultInfo';
import { IKnmViolationLawsuit } from 'APIPackage/KnmModule/interfaces/IKnmViolationLawsuit';
import { deleteObjectProperties } from 'CorePackage/MainModule/common/deleteObjectProperties';
import { formatDate } from 'CorePackage/MainModule/common/formatDate';
import {
  DATE_FORMAT_ISO_FNS,
  DATE_FORMAT_LONG_MINUTE_FNS,
  SYSTEM_DATE_FORMAT_FNS,
} from 'CorePackage/MainModule/constants/dateFormats';
import { IKnmErpFormResultCancellationInfo } from 'KnmErpPackage/KnmErpCancellationInfoModule/interfaces/IKnmErpFormResultCancellationInfo';
import { IKnmErpFormViolationsLawsuit } from 'KnmErpPackage/KnmErpLawsuitsModule/interfaces/IKnmErpFormViolationsLawsuit';
import { IKnmErpFormResult } from 'KnmErpPackage/KnmErpResultsModule/interfaces/IKnmErpFormResult';
import { IKnmErpErvkFormObject } from 'KnmErpPackage/KnmErpSubjectModule/interfaces/IKnmErpErvkFormObject';
import { IKnmErpFormViolation } from 'KnmErpPackage/KnmErpViolationsModule/interfaces/IKnmErpFormViolation';
import { IKnmDistrictSelectOption } from 'CorePackage/CatalogsModule/interfaces/IKnmDistrictSelectOption';
import { IDistrict } from 'APIPackage/KnmModule/interfaces/IDistrict';

import { IKnmErpObjectErvk } from '../../../APIPackage/KnmModule/interfaces/IKnmErpObjectErvk';
import { convertErpChecklistFormToKnmChecklist } from './convertErpChecklistFormToKnmChecklist';
import { IKnmErpFormChecklist } from '../../KnmErpChecklistsModule/interfaces/IKnmErpFormChecklist';
import { IKnmErpObjectErvkSupervision } from '../../../APIPackage/CatalogsModule/interfaces/IKnmErpObjectErvkSupervision';
import { DictionaryUUIDsEnum } from '../../../CorePackage/DictionariesModule/enums/DictionaryUUIDsEnum';

const convertFormDateToParamsDate = (dateString?: string | null) => {
  if (dateString) {
    return formatDate({ date: dateString, dateFormat: SYSTEM_DATE_FORMAT_FNS });
  }

  return null;
};

const convertFormDateTimeToParamsDateTime = (dateString?: string | null) => {
  if (dateString) {
    return formatDate({
      date: dateString,
      dateFormat: DATE_FORMAT_ISO_FNS,
      parseFormat: DATE_FORMAT_LONG_MINUTE_FNS,
    });
  }

  return null;
};

function getResult(
  formObject: IKnmErpErvkFormObject,
  formObjectResults?: IKnmErpFormResult[],
  formViolations?: IKnmErpFormViolation[],
  resultsCancellationInfo?: IKnmErpFormResultCancellationInfo[],
  violationsLawsuits?: IKnmErpFormViolationsLawsuit[],
) {
  const formObjectResult = formObjectResults?.find(objectResult => objectResult.object?.id === formObject.listId);

  const actAddressType: IKnmResult['actAddressType'] = formObjectResult?.actAddressType
    ? deleteObjectProperties(formObjectResult?.actAddressType, ['label', 'value'])
    : undefined;

  const formObjectViolations = formViolations?.filter(
    violation => violation.result?.id === formObjectResult?.object?.id,
  );
  const formObjectResultCancellationInfo = resultsCancellationInfo?.find(
    resultCancellationInfo => resultCancellationInfo.result?.id === formObjectResult?.object?.id,
  );
  const resultAdditionalInfoCancellationItem: IKnmResultInfo | undefined =
    formObjectResultCancellationInfo?.type && formObjectResultCancellationInfo.information
      ? {
          type: formObjectResultCancellationInfo.type,
          information: formObjectResultCancellationInfo.information,
          guid: formObjectResultCancellationInfo.guid ? formObjectResultCancellationInfo.guid : uuidv4(),
        }
      : undefined;

  let resultAdditionalInfo: IKnmResult['additionalInfo'] = formObjectResult?.additionalInfo?.reduce(
    (filteredFormAdditionalInfoItems: IKnmResult['additionalInfo'], formAdditionalInfoItem) => {
      let newFilteredFormAdditionalInfoItems = filteredFormAdditionalInfoItems
        ? [...filteredFormAdditionalInfoItems]
        : null;

      if (formAdditionalInfoItem?.type && formAdditionalInfoItem?.information) {
        if (!newFilteredFormAdditionalInfoItems) {
          newFilteredFormAdditionalInfoItems = [];
        }
        newFilteredFormAdditionalInfoItems.push({
          type: deleteObjectProperties(formAdditionalInfoItem.type, ['label', 'value']),
          information: formAdditionalInfoItem.information,
          guid: formAdditionalInfoItem.guid ? formAdditionalInfoItem.guid : uuidv4(),
        });
      }

      return newFilteredFormAdditionalInfoItems;
    },
    null,
  );

  if (resultAdditionalInfoCancellationItem) {
    if (!resultAdditionalInfo) {
      resultAdditionalInfo = [];
    }
    resultAdditionalInfo.push(resultAdditionalInfoCancellationItem);
  }

  const result: IKnmObjectParams['result'] = formObjectResult
    ? {
        guid: formObjectResult.guid || formObjectResult.listId || uuidv4(),
        actCreateDate: convertFormDateTimeToParamsDateTime(formObjectResult.actCreateDate),
        actAddress: formObjectResult.actAddress || formObjectResult.manualAddress,
        isAddressHazard: !!formObjectResult.isAddressHazard,
        notInFIAS: formObjectResult.notInFIAS,
        district:
          ((formObjectResult.fiasRegion
            ? convertIKnmDistrictSelectOptionToIDistrict(formObjectResult.fiasRegion)
            : null) ||
            (formObjectResult.region ? convertIKnmDistrictSelectOptionToIDistrict(formObjectResult.region) : null)) ??
          null,
        actAddressType: actAddressType ?? null,
        startDate: convertFormDateTimeToParamsDateTime(formObjectResult.startDate),
        durationDays:
          formObjectResult.durationDays && !Number.isNaN(Number(formObjectResult.durationDays))
            ? Number(formObjectResult.durationDays)
            : null,
        durationHours: formObjectResult.durationHours
          ? Number(formObjectResult.durationHours.replace(',', '.')) || null
          : null,
        actWasNotRead: formObjectResult.additionalInfo?.find(
          additionalInfoItem => additionalInfoItem?.type?.code === KnmResultInfoTypeCodesEnum.ACT_WAS_NOT_READ_NOTE,
        )?.actWasNotRead,
        inspectors: formObjectResult.inspectors?.reduce((filteredInspectors: IKnmResult['inspectors'], inspector) => {
          let newFilteredInspectors = filteredInspectors ? [...filteredInspectors] : null;

          if (inspector) {
            if (!newFilteredInspectors) {
              newFilteredInspectors = [];
            }
            newFilteredInspectors.push({
              ...inspector,
              type: deleteObjectProperties(inspector.type, ['label', 'value']),
            });
          }

          return newFilteredInspectors?.map(filteredInspector => {
            return {
              ...filteredInspector,
              guid: filteredInspector.guid || uuidv4(),
            };
          });
        }, null),
        delegates: formObjectResult.delegates?.reduce((filteredDelegates: IKnmResult['delegates'], delegate) => {
          let newFilteredDelegates = filteredDelegates ? [...filteredDelegates] : null;

          if (delegate) {
            if (!newFilteredDelegates) {
              newFilteredDelegates = [];
            }
            newFilteredDelegates.push({
              ...delegate,
              type: deleteObjectProperties(delegate.type, ['label', 'value']),
            });
          }

          return newFilteredDelegates?.map(filteredDelegate => {
            return {
              ...filteredDelegate,
              guid: filteredDelegate.guid || uuidv4(),
            };
          });
        }, null),
        additionalInfo: resultAdditionalInfo?.map(additionalInfo => {
          return {
            ...additionalInfo,
            guid: additionalInfo.guid || uuidv4(),
          };
        }),
        violations: formObjectViolations?.map(formViolation => ({
          ...deleteObjectProperties(formViolation, ['result', 'isNew', 'listId']),
          type: formViolation.type && deleteObjectProperties(formViolation.type, ['label', 'value']),
          legalBases: formViolation.legalBases?.map(legalBasis => ({
            guid: legalBasis.guid || uuidv4(),
            ...deleteObjectProperties(legalBasis, ['id']),
          })),
          injunctions: formViolation.injunctions?.reduce(
            (filteredInjunctions: IKnmInjunction[] | null, formInjunction) => {
              let newFilteredInjunctions = filteredInjunctions ? [...filteredInjunctions] : null;

              if (formInjunction) {
                if (!newFilteredInjunctions) {
                  newFilteredInjunctions = [];
                }
                newFilteredInjunctions.push({
                  ...formInjunction,
                  appointmentDate: convertFormDateToParamsDate(formInjunction.appointmentDate),
                  executionDeadline: convertFormDateToParamsDate(formInjunction.executionDeadline),
                });
              }

              return newFilteredInjunctions?.map(injunction => {
                return {
                  ...injunction,
                  guid: injunction.guid || uuidv4(),
                };
              });
            },
            null,
          ),
          lawsuits: violationsLawsuits?.reduce((filteredLawsuits: IKnmViolationLawsuit[] | null, violationLawsuits) => {
            let newFilteredLawsuits = filteredLawsuits ? [...filteredLawsuits] : null;

            if (violationLawsuits.violation?.id === formViolation.listId && violationLawsuits.lawsuits?.length) {
              newFilteredLawsuits = violationLawsuits.lawsuits?.map(lawsuit => ({
                ...lawsuit,
                lawsuitType: deleteObjectProperties(lawsuit.lawsuitType, ['label', 'value']),
              }));
            }

            return newFilteredLawsuits?.map(lawsuit => {
              return {
                ...lawsuit,
                guid: lawsuit.guid || uuidv4(),
              };
            });
          }, null),
          guid: formViolation.guid || uuidv4(),
        })),
      }
    : null;

  return result;
}

const convertIKnmDistrictSelectOptionToIDistrict = (opt: IKnmDistrictSelectOption): IDistrict => {
  return {
    ...opt,
  };
};

export function convertErpObjectErvkFormToKnmObjectErvk(
  formObject: IKnmErpErvkFormObject,
  formObjectResults?: IKnmErpFormResult[],
  formViolations?: IKnmErpFormViolation[],
  resultsCancellationInfo?: IKnmErpFormResultCancellationInfo[],
  violationsLawsuits?: IKnmErpFormViolationsLawsuit[],
  formChecklists?: IKnmErpFormChecklist[],
): IKnmErpObjectErvk {
  const checklists: IKnmObjectParams['checklists'] = formChecklists
    ?.filter(formChecklist => formChecklist.object?.id === formObject.listId)
    .map(formChecklist => convertErpChecklistFormToKnmChecklist(formChecklist));

  return {
    address: formObject.address || null,
    addressFns: formObject.addressFns || null,
    autocomplete: typeof formObject.autocomplete === 'boolean' ? formObject.autocomplete : null,
    checklists: checklists || null,
    dateCreate: formObject.dateCreate || null,
    ervkId: formObject.ervkId || null,
    ervkNumber: formObject.ervkNumber || null,
    fiasRegion: formObject.fiasRegion ? deleteObjectProperties(formObject.fiasRegion, ['label', 'value']) : null,
    geolocation: formObject.geolocation || null,
    guid: formObject.guid || uuidv4(),
    houseGuid: formObject.houseGuid || null,
    isAddressHazard: !!formObject.isAddressHazard,
    manualAddress: formObject.manualAddress || null,
    name: formObject.name || null,
    notInFIAS: formObject.notInFIAS || null,
    objectKind: formObject.objectKind?.value || null,
    objectSubKind: formObject.objectSubKind?.value || null,
    objectType: formObject.objectType?.value || null,
    organizationErvkId: formObject.organizationErvkId || null,
    region: formObject.region ? deleteObjectProperties(formObject.region, ['label', 'value']) : null,
    regionName: formObject.regionName || null,
    objectResult:
      getResult(formObject, formObjectResults, formViolations, resultsCancellationInfo, violationsLawsuits) || null,
    riskCategory: formObject.riskCategory?.value
      ? {
          dictId: formObject.riskCategory.value.dictId as DictionaryUUIDsEnum,
          dictVersionId: formObject.riskCategory.value.dictVersionId,
        }
      : null,
    roomGuid: formObject.roomGuid || null,
    steadGuid: formObject.steadGuid || null,
    streetGuid: formObject.streetGuid || null,
    supervisions:
      formObject.supervisions?.map(formSupervision => {
        const supervision: IKnmErpObjectErvkSupervision = {
          numGuid: formSupervision.listId || uuidv4(),
          supervisionType: formSupervision.supervision.value,
        };

        return supervision;
      }) || null,
  };
}
